import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { EDIT_PROFILE } from "@/constants/testIds";

export const EditProfileDialog = ({ open, onOpenChange }) => {
  const { profile, request, apply } = useProfile();
  const [form, setForm] = useState({ display_name: "", username: "", bio: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && profile) { setForm({ display_name: profile.display_name, username: profile.username, bio: profile.bio || "" }); setError(""); }
  }, [open, profile]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      apply(await request("patch", "/me", form));
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={EDIT_PROFILE.dialog} className="bg-nw-bg2 border-white/10 text-slate-100 max-w-[92vw] sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl chrome-text">Edit profile</DialogTitle>
          <DialogDescription className="text-slate-400">Update how the wave sees you.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Display name</span>
            <input data-testid={EDIT_PROFILE.displayName} required maxLength={50} value={form.display_name} onChange={set("display_name")} className="nw-input" />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Username</span>
            <input data-testid={EDIT_PROFILE.username} required pattern="[a-zA-Z0-9_]{3,24}" value={form.username} onChange={set("username")} className="nw-input" />
          </label>
          <label className="block space-y-1.5">
            <span className="flex justify-between text-xs font-semibold uppercase tracking-widest text-slate-400">Bio <span className="font-mono normal-case tracking-normal text-slate-500">{form.bio.length}/300</span></span>
            <textarea data-testid={EDIT_PROFILE.bio} rows={4} maxLength={300} value={form.bio} onChange={set("bio")} className="nw-input resize-none" placeholder="Tell fans about your AI sound, tools and vision..." />
          </label>
          {error && <p data-testid={EDIT_PROFILE.error} className="text-sm text-red-400">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button type="button" data-testid={EDIT_PROFILE.cancel} onClick={() => onOpenChange(false)} className="btn-chrome flex-1 py-3 text-sm">Cancel</button>
            <button data-testid={EDIT_PROFILE.save} disabled={busy} className="btn-red flex-1 py-3 text-sm disabled:opacity-60">
              {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Save"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
